/* eslint-disable import/order */
import { useEffect, useRef, useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import { getUserInfo } from '@utils/Util';
import {
	AUTO_COMPLETE_PREV_TASK,
	STAGE_START_LIST,
	TASK_REVIEW_STATUS,
	TASK_REVIEW_STATUS_NEW,
	TP_OWNER_SATISFICATION_COMMENT,
	WAS_SAMPLE_ON_TIME,
	WORK_COMPLEATE_LIST,
	TASK_PRIORITY_OPTIONS,
	CLOSE_REASON_LIST
} from '@/constants/constant';
import { TASK_VALUE_TYPE } from '@/features/sampleDevelopment/constants/taskValueTypeConstants';
/* eslint-enable import/order */

import CustomDropdown from '@/components/CustomDropdown';
import ModalContainer from '@/components/modals/ModalContainer';
import { useGetAllBrandsQuery, useGetAllUserQuery, useGetEnumsQuery } from '@/api/commonApis';
import {
	useGetConnectableDealListQuery,
	useGetProductActionListQuery,
	useProductDevelopmentAddMutation
} from '@/features/Scan/api/scanApi';
import { toastError, toastSuccess } from '@/components/Toast';
import LoaderComponent from '@/components/LoaderComponent';
import ModalHeader from '@/components/modals/ModalHeader';
import InputField from '@/components/InputField';
import { isAdmin, replaceUnderLine } from '@/utils/Util';

const DESIGN_MANAGER_MANDATORY_STATUSES = ['DESIGN_REJECTED', 'CHANGE_REQUIRED', 'DEVELOP_LATER'];

const INITIAL_STATE = {
	task: null,
	rejectReason: '',
	sewingQty: 0,
	assignedTo: null,
	reviewStatus: null,
	brand: null,
	deal: null,
	comment: '',
	alreadyCompletedTaskIds: [], // Auto complete previous task
	partialDevelopmentWorkCompletedUpto: null, //Work completed till*
	partialDevelopmentWorkStartedFrom: null, // Select starting stage,
	closedReason: null,
	requiredDate: null, //sample handle planning date
	tpOwnerIssueComment: '',
	tpOwnerIssueCommentDetails: '',
	hiddenQcComment: ''
};

const AddTaskModal = ({
	onClose,
	productIdList = [],
	onSuccess = () => {},
	onError = () => {},
	params = {},
	selectedAction = null,
	journeyType = '',
	excludeTasks = [],
	quoteRequestId,
	excludeJourneyTypes = 'COSTING'
}) => {
	const [taskValues, setTaskValues] = useState(INITIAL_STATE);
	const [errors, setErrors] = useState(null);

	const commentRef = useRef(null);

	useEffect(() => {
		if (selectedAction) {
			setTaskValues((prev) => ({
				...prev,
				task: selectedAction
			}));
		}
	}, [selectedAction?.id]);

	const { data: productActionList } = useGetProductActionListQuery({
		params: {
			type: 'ACTION',
			...params,
			excludeTaskValueTypes: excludeTasks.join(','),
			journeyType,
			...(excludeJourneyTypes && { excludeJourneyTypes })
		}
	});
	const [productDevelopmentAdd, { isLoading }] = useProductDevelopmentAddMutation();
	const { data: userList } = useGetAllUserQuery({
		params: { page: 0, size: 1000 }
	});
	const { data: fitTypes } = useGetEnumsQuery({ path: 'info.nitex.app.product.SampleDevelopmentType' });
	const { data: reviewStatusItems } = useGetEnumsQuery({
		path: 'info.nitex.app.product.DesignManagerReviewStatus'
	});
	const { data: selectDesignManagerReviewStatus } = useGetEnumsQuery({
		path: 'info.nitex.app.product.DesignManagerReviewIssueArea'
	});

	const { data: IssueType } = useGetEnumsQuery({
		path: 'info.nitex.app.product.IssueType'
	});

	const handleChange = (value, name) => {
		setTaskValues((prev) => ({
			...prev,
			[name]: value
		}));
		setErrors((prev) => ({
			...prev,
			[name]: ''
		}));
	};

	const { data: brandsList } = useGetAllBrandsQuery({
		params: { page: 0, size: 1000 }
	});

	const isValidFields = () => {
		const newErrors = {};
		if (!taskValues?.task?.taskValueType) {
			newErrors.task = 'Task name is Required.';
		}
		if ((isSampleReviewedByTechnical() || isQCReview()) && !taskValues?.reviewStatus?.id) {
			newErrors.reviewStatus = 'Review status is required.';
		}
		if (
			(isSampleReviewedByTechnical() || isQCReview()) &&
			['REJECTED'].includes(taskValues?.reviewStatus?.id) &&
			!taskValues?.rejectReason
		) {
			newErrors.rejectReason = 'Reject reason is Required.';
		}
		if (isSewingCompleted() && !taskValues?.assignedTo) {
			newErrors.assignedTo = 'Sewing completed by is required.';
		}
		if (isSewingCompleted() && !taskValues?.sewingQty) {
			newErrors.sewingQty = 'Sewing quantity is required.';
		}
		if (isSampleHandlePlanningDate() && !taskValues?.requiredDate) {
			newErrors.requiredDate = 'Required date is required.';
		}
		if ((isPresentedToTheBuyer() || isBuyerInitiallySelected()) && !taskValues?.brand) {
			newErrors.brand = 'Brand name is required.';
		}
		if (isSampleReviewByTP() && !taskValues?.tpOwnerCommentSampleDeliveredOnTime?.value) {
			newErrors.tpOwnerCommentSampleDeliveredOnTime = 'Delivery timeline is required.';
		}
		if (isSampleReviewByTP() && !taskValues?.tpOwnerSatisfactionComment?.value) {
			newErrors.tpOwnerSatisfactionComment = 'Comment is required.';
		}
		if (isSampleReviewByTP() && isSmapleSatisfied() && !taskValues?.tpOwnerIssueTypeIds?.length) {
			newErrors.tpOwnerIssueTypeIds = 'Please select at least one issue type.';
		}
		if (
			(isSampleReviewByTP() && isSmapleSatisfied() && !taskValues?.tpOwnerIssueComment?.trim()) ||
			(isCommercialApproval() && !taskValues?.tpOwnerIssueComment?.trim())
		) {
			newErrors.tpOwnerIssueComment = 'Please explain the issue.';
		}
		if (isSampleReviewByDesigner() && !taskValues?.reviewStatusItems?.id) {
			newErrors.reviewStatusItems = 'Review status is required.';
		}
		if (
			isSampleReviewByDesigner() &&
			DESIGN_MANAGER_MANDATORY_STATUSES.includes(taskValues?.reviewStatusItems?.id) &&
			(!taskValues?.selectDesignManagerReviewStatus ||
				taskValues?.selectDesignManagerReviewStatus?.length === 0)
		) {
			newErrors.selectDesignManagerReviewStatus = 'Please select at least one issue/area to change.';
		}
		if (
			(isPartialDevCompleated() || isPartialFactoryDev()) &&
			!taskValues?.partialDevelopmentWorkCompletedUpto?.value
		) {
			newErrors.partialDevelopmentWorkCompletedUpto = 'Stage name is required.';
		}
		if (isSetPriority() && !taskValues?.priorityType?.value) {
			newErrors.priorityType = 'Priority type is required.';
		}
		if (isConnectProductToDeal() && !taskValues?.deal?.dealId) {
			newErrors.deal = 'Deal is required.';
		}
		if (isCostingClosedWithoutOrder() && !taskValues?.closedReason?.value) {
			newErrors.closedReason = 'Reason is required.';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = () => {
		if (!isValidFields()) {
			return;
		}

		const requestBody = {
			productIdList,
			productActionId: taskValues?.task?.id,
			taskValueType: taskValues?.task?.taskValueType?.constant,
			brandId: taskValues?.brand?.id,
			sampleReviewStatus: taskValues?.reviewStatus?.id,
			sewingQuantity: taskValues?.sewingQty,
			rejectionReason: taskValues?.rejectReason
				? taskValues?.rejectReason
				: taskValues?.closedReason?.id || taskValues?.closedReason?.value,
			assignedToId: taskValues?.assignedTo?.id,
			note: taskValues?.comment,
			orderSampleDevelopmentType: taskValues?.fitTypes?.id,
			alreadyCompletedTaskIds: taskValues?.alreadyCompletedTaskIds?.map((item) => item?.id) || null,
			partialDevelopmentWorkCompletedUpto:
				taskValues?.partialDevelopmentWorkCompletedUpto?.value || null,
			partialDevelopmentWorkStartedFrom: taskValues?.partialDevelopmentWorkStartedFrom?.value || null,
			tpOwnerCommentSampleDeliveredOnTime:
				taskValues?.tpOwnerCommentSampleDeliveredOnTime?.value || null,
			tpOwnerSatisfactionComment: taskValues?.tpOwnerSatisfactionComment?.value || null,
			taskPriority: taskValues?.priorityType?.id,
			quoteRequestId,
			dealId: taskValues?.deal?.dealId || null,
			requiredDate: taskValues?.requiredDate || null,

			designManagerReviewStatus: taskValues?.reviewStatusItems?.id,
			designManagerReviewIssueAreas:
				taskValues?.selectDesignManagerReviewStatus?.map((item) => item?.constant) || null,
			tpOwnerIssueTypeIds: taskValues?.tpOwnerIssueTypeIds?.map((item) => item?.constant) || null,
			tpOwnerIssueComment: taskValues?.tpOwnerIssueComment || null,
			tpOwnerIssueCommentDetails: taskValues?.tpOwnerIssueCommentDetails || null,
			hiddenQcComment: taskValues?.hiddenQcComment || null
		};

		const filteredRequestBody = Object.fromEntries(
			Object.entries(requestBody).filter(
				([, value]) =>
					value !== null &&
					value !== undefined &&
					value !== '' &&
					value !== 0 &&
					!(Array.isArray(value) && value.length === 0)
			)
		);

		productDevelopmentAdd({ body: filteredRequestBody })
			.unwrap()
			.then((res) => {
				if (!res?.success) {
					if (productIdList?.length === 1) {
						const payload = { ...res?.payload };
						toastError(Object.values(payload)[0] || 'Something went wrong!');
					} else {
						toastError(res?.message || 'Something went wrong!');
					}

					const ids = Object.keys(res?.payload);
					onError(ids?.map((item) => parseInt(item)));
					onClose();
					return;
				}

				onSuccess();
				onError([]);
				toastSuccess('Updated successfully');
				onClose();
			})
			.catch((error) => {
				toastError(error?.data?.message || 'Something went wrong');
			});
	};

	const isSewingCompleted = () =>
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.SAMPLE_SEWING;
	const isSampleHandlePlanningDate = () =>
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.SAMPLE_HANDOVER_TO_OWNER_PLANNING;

	const isQCReview = () =>
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.BUYER_SAMPLE_REVIEW ||
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.SAMPLE_REVIEW_BY_PD ||
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.FACTORY_DEVELOPMENT_REVIEW_BY_GT ||
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.IN_HOUSE_DEVELOPMENT_REVIEW_BY_GT ||
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.IN_HOUSE_DEVELOPMENT_REVIEW_BY_BUYER;

	const isSmapleSatisfied = () =>
		taskValues?.tpOwnerSatisfactionComment?.constant === TP_OWNER_SATISFICATION_COMMENT.IM_SATISFIED ||
		taskValues?.tpOwnerSatisfactionComment?.constant ===
			TP_OWNER_SATISFICATION_COMMENT.IM_VERY_SATISFIED_MEETS_EXPECTATIONS;

	const isSampleReviewedByTechnical = () =>
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.SAMPLE_REVIEW_QC;

	const isPresentedToTheBuyer = () =>
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.PRESENTED_TO_BUYER;

	const isBuyerInitiallySelected = () =>
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.SELECTED_BY_BUYER ||
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.CONFIRMED_BY_BUYER;

	const isRejected = () => isSampleReviewedByTechnical();
	const isOrderSampleReview = () =>
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.ORDER_SAMPLE_REVIEW ||
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.SHIPMENT_SAMPLE__REVIEW ||
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.ORDER_FIT_SAMPLE_REVIEW_BY_GT ||
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.ORDER_PP_SAMPLE_REVIEW_BY_GT;

	const isPartialDevCompleated = () =>
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.PARTIAL_IN_HOUSE_SAMPLE_DEVELOPMENT;

	const isInHouseSampleDev = () =>
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.IN_HOUSE_SAMPLE_DEVELOPMENT_START;

	const isPartialFactoryDev = () =>
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.PARTIAL_FACTORY_SAMPLE_DEVELOPMENT;

	const isSampleReviewByTP = () =>
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.SAMPLE_REVIEW_TP_OWNER;

	const isSampleReviewByDesigner = () =>
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.DESIGN_REVIEW_BY_TEAM_LEAD;

	const isPatternMasterCreate = () =>
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.ASSIGN_PATTERN_MASTER;
	const isAssignSewing = () =>
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.ASSIGN_SEWING_MAN;
	const isCommercialApproval = () =>
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.NEED_PD_COMMERCIAL_APPROVAL;

	const isConnectProductToDeal = () =>
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.CONNECT_PRODUCT_TO_DEAL;

	const { data: connectableDealList } = useGetConnectableDealListQuery(undefined, {
		skip: !isConnectProductToDeal()
	});

	const dealOptions = (connectableDealList || []).map((item) => ({
		...item,
		id: item?.dealId,
		value: item?.dealName
	}));

	const reviewStatusFilter = () => {
		return [
			TASK_VALUE_TYPE.SHIPMENT_SAMPLE_REVIEW,
			TASK_VALUE_TYPE.ORDER_FIT_SAMPLE_REVIEW_BY_GT,
			TASK_VALUE_TYPE.ORDER_PP_SAMPLE_REVIEW_BY_GT,
			TASK_VALUE_TYPE.FACTORY_DEVELOPMENT_REVIEW_BY_GT
		].includes(taskValues?.task?.taskValueType?.constant);
	};
	const isSetPriority = () =>
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.TASK_PRIORITY_UPDATE;

	const isClosedReason = () =>
		taskValues?.task?.taskValueType?.constant ===
			TASK_VALUE_TYPE.IN_HOUSE_DEVELOPMENT_CLOSED_WITHOUT_ORDER ||
		taskValues?.task?.taskValueType?.constant ===
			TASK_VALUE_TYPE.FACTORY_DEVELOPMENT_CLOSED_WITHOUT_ORDER ||
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.COSTING_CLOSED_WITHOUT_ORDER;

	const isCostingClosedWithoutOrder = () =>
		taskValues?.task?.taskValueType?.constant === TASK_VALUE_TYPE.COSTING_CLOSED_WITHOUT_ORDER;

	const selectedDesignManagerReviewStatusValue = (taskValues?.selectDesignManagerReviewStatus || []).map(
		(item) => ({
			...item,
			id: item?.constant,
			value: item?.constant,
			label: item?.name || replaceUnderLine(item?.constant)
		})
	);

	const designManagerReviewStatusOptions = (selectDesignManagerReviewStatus || []).map((item) => ({
		...item,
		id: item?.constant,
		value: item?.constant,
		label: item?.name || replaceUnderLine(item?.constant)
	}));

	const selectedIssueTypeValue = (taskValues?.tpOwnerIssueTypeIds || []).map((item) => ({
		...item,
		id: item?.constant,
		value: item?.constant,
		label: item?.name || replaceUnderLine(item?.constant)
	}));

	const issueTypeOptions = (IssueType || []).map((item) => ({
		...item,
		id: item?.constant,
		value: item?.constant,
		label: item?.name || replaceUnderLine(item?.constant)
	}));

	const handleTextareaHeight = (e) => {
		e.target.style.height = 'auto';
		e.target.style.height = `${e.target.scrollHeight}px`;
	};

	const handleTpOwnerIssueCommentChange = (e) => {
		handleTextareaHeight(e);
		handleChange(e.target.value, 'tpOwnerIssueComment');
	};
	const hiddenQcComment = (e) => {
		handleTextareaHeight(e);
		handleChange(e.target.value, 'hiddenQcComment');
	};

	const userInfo = getUserInfo();

	const filteredProductActionList = (productActionList || [])
		.filter((item) => !item?.isTeamLeadOnly || userInfo?.isTeamLead)
		.map((item) => ({
			...item,
			value: item?.name
		}));
	return (
		<LoaderComponent loading={isLoading}>
			<ModalContainer className="update-sample-and-sales-modal">
				<div className="popup-wrapper width-420-modal shadow-16dp">
					<div className="common-popup update-sample-and-sales-modal body-scroll">
						<ModalHeader title="Update task" onClose={onClose} />
						<div className={`common-popup-body ${isSampleReviewByTP() && 'scroll-y-label'} `}>
							<div className="row">
								<div className="col-md-12 pr-2">
									<div className={`form-group ${selectedAction ? 'dissabled-field' : ''}`}>
										<label htmlFor="task">
											{selectedAction ? 'Selected task*' : 'Select task*'}{' '}
										</label>
										<CustomDropdown
											items={filteredProductActionList}
											title="Select task"
											selectedItem={taskValues?.task?.name}
											onItemClick={(item) => handleChange(item, 'task')}
										/>
										{errors?.task && <span className="error">{errors?.task}</span>}
									</div>
								</div>
								{isSampleReviewByTP() && (
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<label htmlFor="task">Was the sample delivered on time?*</label>
											<CustomDropdown
												items={WAS_SAMPLE_ON_TIME}
												title="Select"
												selectedItem={
													taskValues?.tpOwnerCommentSampleDeliveredOnTime?.value
												}
												onItemClick={(item) =>
													handleChange(item, 'tpOwnerCommentSampleDeliveredOnTime')
												}
											/>
											{errors?.tpOwnerCommentSampleDeliveredOnTime && (
												<span className="error">
													{errors?.tpOwnerCommentSampleDeliveredOnTime}
												</span>
											)}
										</div>
									</div>
								)}
								{isSampleReviewByTP() && (
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<label htmlFor="task">
												How satisfied are you with the sample?*
											</label>
											<CustomDropdown
												items={TP_OWNER_SATISFICATION_COMMENT}
												title="Select"
												selectedItem={taskValues?.tpOwnerSatisfactionComment?.value}
												onItemClick={(item) =>
													handleChange(item, 'tpOwnerSatisfactionComment')
												}
											/>
											{errors?.tpOwnerSatisfactionComment && (
												<span className="error">
													{errors?.tpOwnerSatisfactionComment}
												</span>
											)}
										</div>
									</div>
								)}
								{isSampleReviewByTP() && isSmapleSatisfied() && (
									<div className="col-md-12 pr-2">
										<div className="form-group no-shadow-react-select">
											<label htmlFor="IssueType">Select the issue*</label>
											<CreatableSelect
												placeholder="Select"
												onChange={(value) =>
													handleChange(value, 'tpOwnerIssueTypeIds')
												}
												value={selectedIssueTypeValue}
												options={issueTypeOptions}
												classNamePrefix="react-select"
												isMulti
												isValidNewOption={() => false}
											/>
											{errors?.tpOwnerIssueTypeIds && (
												<span className="error">{errors?.tpOwnerIssueTypeIds}</span>
											)}
										</div>
									</div>
								)}
								{isSampleReviewByTP() && isSmapleSatisfied() && (
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<label htmlFor="tpOwnerIssueComment">Explain the issue*</label>
											<textarea
												name="tpOwnerIssueComment"
												id="tpOwnerIssueComment"
												value={taskValues?.tpOwnerIssueComment || ''}
												style={{
													height: '55px',
													resize: 'none',
													overflow: 'hidden'
												}}
												rows="1"
												onChange={handleTpOwnerIssueCommentChange}
											/>
											{errors?.tpOwnerIssueComment && (
												<span className="error">{errors?.tpOwnerIssueComment}</span>
											)}
										</div>
									</div>
								)}
								{isSampleReviewByTP() && (
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<InputField
												type="text"
												name="tpOwnerIssueCommentDetails"
												id="tpOwnerIssueCommentDetails"
												value={taskValues?.tpOwnerIssueCommentDetails}
												onChangeHandler={(e) =>
													handleChange(e.target.value, 'tpOwnerIssueCommentDetails')
												}
												labelTitle="Feedback document link"
											/>
											{errors?.tpOwnerIssueCommentDetails && (
												<span className="error">
													{errors?.tpOwnerIssueCommentDetails}
												</span>
											)}
										</div>
									</div>
								)}

								{isCommercialApproval() && (
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<label htmlFor="tpOwnerIssueComment">Explain the issue*</label>
											<textarea
												name="tpOwnerIssueComment"
												id="tpOwnerIssueComment"
												value={taskValues?.tpOwnerIssueComment || ''}
												style={{
													height: '55px',
													resize: 'none',
													overflow: 'hidden'
												}}
												rows="1"
												onChange={handleTpOwnerIssueCommentChange}
											/>
											{errors?.tpOwnerIssueComment && (
												<span className="error">{errors?.tpOwnerIssueComment}</span>
											)}
										</div>
									</div>
								)}

								{isSampleReviewByDesigner() && (
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<label htmlFor="task">Review status*</label>
											<CustomDropdown
												items={reviewStatusItems?.map((item) => ({
													...item,
													id: item?.constant,
													value: replaceUnderLine(item?.name)
												}))}
												title="Select review status"
												selectedItem={taskValues?.reviewStatusItems?.value}
												onItemClick={(item) =>
													handleChange(item, 'reviewStatusItems')
												}
											/>
											{errors?.reviewStatusItems && (
												<span className="error">{errors?.reviewStatusItems}</span>
											)}
										</div>
									</div>
								)}
								{isPatternMasterCreate() && (
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<label htmlFor="task">Select pattern master</label>
											<CustomDropdown
												items={userList
													?.filter(
														(user) => user?.primaryUserType === 'PATTERN_MASTER'
													)
													?.map((item) => ({
														...item,
														id: item?.id,
														value: item?.name
													}))}
												title="Select pattern master"
												selectedItem={taskValues?.assignedTo?.name}
												onItemClick={(item) => handleChange(item, 'assignedTo')}
											/>
											{errors?.assignedTo && (
												<span className="error">{errors?.assignedTo}</span>
											)}
										</div>
									</div>
								)}

								{isAssignSewing() && (
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<label htmlFor="brand">Select sewing men</label>
											<CustomDropdown
												items={userList
													?.filter((user) => user?.primaryUserType === 'SEWING_MAN')
													?.map((item) => ({
														...item,
														id: item?.id,
														value: item?.name
													}))}
												title="Select sewing men"
												selectedItem={taskValues?.assignedTo?.name}
												onItemClick={(item) => handleChange(item, 'assignedTo')}
											/>
											{errors?.assignedTo && (
												<span className="error">{errors?.assignedTo}</span>
											)}
										</div>
									</div>
								)}

								{isSampleReviewByDesigner() && (
									<div className="col-md-12 pr-2">
										<div className="form-group no-shadow-react-select">
											<label htmlFor="task">
												Select issue/where to change
												{DESIGN_MANAGER_MANDATORY_STATUSES.includes(
													taskValues?.reviewStatusItems?.id
												)
													? '*'
													: ''}
											</label>
											<CreatableSelect
												placeholder="Select"
												onChange={(value) =>
													handleChange(value, 'selectDesignManagerReviewStatus')
												}
												value={selectedDesignManagerReviewStatusValue}
												options={designManagerReviewStatusOptions}
												classNamePrefix="react-select"
												isMulti
												isValidNewOption={() => false}
											/>
											{errors?.selectDesignManagerReviewStatus && (
												<span className="error">
													{errors?.selectDesignManagerReviewStatus}
												</span>
											)}
										</div>
									</div>
								)}

								{(isPartialDevCompleated() || isPartialFactoryDev()) && (
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<label htmlFor="task">Work completed till*</label>
											<CustomDropdown
												items={WORK_COMPLEATE_LIST}
												title="Select"
												selectedItem={
													taskValues?.partialDevelopmentWorkCompletedUpto?.value
												}
												onItemClick={(item) =>
													handleChange(item, 'partialDevelopmentWorkCompletedUpto')
												}
											/>
											{errors?.partialDevelopmentWorkCompletedUpto && (
												<span className="error">
													{errors?.partialDevelopmentWorkCompletedUpto}
												</span>
											)}
										</div>
									</div>
								)}
								{isInHouseSampleDev() && (
									<div className="col-md-12 pr-2 ">
										<div className="form-group ">
											<label htmlFor="task">Select starting stage</label>
											<CustomDropdown
												items={STAGE_START_LIST}
												title="Select"
												selectedItem={
													taskValues?.partialDevelopmentWorkStartedFrom?.value
												}
												onItemClick={(item) => {
													handleChange(item, 'partialDevelopmentWorkStartedFrom');
													setTaskValues((prev) => ({
														...prev,
														alreadyCompletedTaskIds: AUTO_COMPLETE_PREV_TASK
													}));
												}}
											/>
											{errors?.task && <span className="error">{errors?.task}</span>}
										</div>
									</div>
								)}
								{isInHouseSampleDev() && (
									<div className="col-md-12 pr-2">
										<div className="form-group no-shadow-react-select">
											<label htmlFor="task">Auto complete previous task</label>
											<CreatableSelect
												placeholder="Select"
												onChange={(value) =>
													setTaskValues((prev) => ({
														...prev,
														alreadyCompletedTaskIds: value
													}))
												}
												value={taskValues?.alreadyCompletedTaskIds}
												options={AUTO_COMPLETE_PREV_TASK}
												classNamePrefix="react-select"
												isMulti
												isValidNewOption={() => false}
											/>
											{errors?.task && <span className="error">{errors?.task}</span>}
										</div>
									</div>
								)}
								{/* if Task name is "sewing compleated" then show this */}
								{isSewingCompleted() && (
									<>
										<div className="col-md-12 pr-2">
											<div className="form-group">
												<label htmlFor="brand">Sewing completed by*</label>
												<CustomDropdown
													items={userList
														?.filter(
															(user) => user?.primaryUserType === 'SEWING_MAN'
														)
														?.map((item) => ({
															...item,
															id: item?.id,
															value: item?.name
														}))}
													title="Select sewing compleated by"
													selectedItem={taskValues?.assignedTo?.name}
													onItemClick={(item) => handleChange(item, 'assignedTo')}
												/>
												{errors?.assignedTo && (
													<span className="error">{errors?.assignedTo}</span>
												)}
											</div>
										</div>
										{/* if Task name is "sewing compleated" then show this */}
										<div className="col-md-12 pr-2">
											<InputField
												placeholder="Sewing Qty"
												type="number"
												value={taskValues?.sewingQty}
												onChange={(e) => handleChange(e.target.value, 'sewingQty')}
												min={0}
												step={1}
												labelTitle="Sewing Qty*"
												errors={errors?.sewingQty}
											/>
										</div>
									</>
								)}

								{isSampleHandlePlanningDate() && (
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<label htmlFor="brand">Select date</label>
											<InputField
												placeholder="Sample handle planning date"
												type="date"
												value={taskValues?.requiredDate}
												onChange={(e) => handleChange(e.target.value, 'requiredDate')}
											/>
										</div>
									</div>
								)}

								{(isSampleReviewedByTechnical() || isQCReview()) && (
									<>
										{/* if Task name is "Sample reviewed by technical" then show this */}
										<div className="col-md-12 pr-2">
											<div className="form-group">
												<label htmlFor="brand">Review status*</label>
												<CustomDropdown
													items={TASK_REVIEW_STATUS?.map((item) => ({
														...item,
														value: item?.value
													}))}
													title="Select review status"
													selectedItem={taskValues?.reviewStatus?.value}
													onItemClick={(item) => handleChange(item, 'reviewStatus')}
												/>
												{errors?.reviewStatus && (
													<span className="error">{errors?.reviewStatus}</span>
												)}
											</div>
										</div>
										{(((isRejected() || isQCReview()) &&
											taskValues?.reviewStatus?.id === 'REJECTED') ||
											taskValues?.reviewStatus?.id === 'COMMERCIALLY_APPROVED' ||
											taskValues?.reviewStatus?.id === 'APPROVED_WITH_COMMENT') && (
											<>
												{/* issue type hidden */}
												<div className="col-md-12 pr-2">
													<div className="form-group no-shadow-react-select">
														<label htmlFor="IssueType">Issue type (Hidden)</label>
														<CreatableSelect
															placeholder="Select"
															onChange={(value) =>
																handleChange(value, 'tpOwnerIssueTypeIds')
															}
															value={selectedIssueTypeValue}
															options={issueTypeOptions}
															classNamePrefix="react-select"
															isMulti
															isValidNewOption={() => false}
														/>
													</div>
												</div>
											</>
										)}

										{(isRejected() || isQCReview()) &&
											taskValues?.reviewStatus?.id === 'REJECTED' && (
												<>
													{/* if Task name is "Sample reviewed by technical and status is rejected" then show this */}
													<div className="col-md-12 pr-2">
														<InputField
															placeholder="Explain the reason"
															type="text"
															value={taskValues?.rejectReason}
															onChange={(e) =>
																handleChange(e.target.value, 'rejectReason')
															}
															labelTitle="Explain the reason*"
															errors={errors?.rejectReason}
														/>
													</div>
												</>
											)}
										{(((isRejected() || isQCReview()) &&
											taskValues?.reviewStatus?.id === 'REJECTED') ||
											taskValues?.reviewStatus?.id === 'COMMERCIALLY_APPROVED' ||
											taskValues?.reviewStatus?.id === 'APPROVED_WITH_COMMENT') && (
											<>
												{/* issue comment hidden */}
												<div className="col-md-12 pr-2">
													<div className="form-group">
														<label htmlFor="hiddenQcComment">
															Hidden Comment
														</label>
														<textarea
															name="hiddenQcComment"
															id="hiddenQcComment"
															value={taskValues?.hiddenQcComment || ''}
															style={{
																height: '55px',
																resize: 'none',
																overflow: 'hidden'
															}}
															rows="1"
															onChange={hiddenQcComment}
														/>
														{errors?.hiddenQcComment && (
															<span className="error">
																{errors?.hiddenQcComment}
															</span>
														)}
													</div>
												</div>
											</>
										)}
									</>
								)}
								{isClosedReason() && (
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<label htmlFor="brand">
												Select Reason{isCostingClosedWithoutOrder() ? '*' : ''}
											</label>
											<CustomDropdown
												items={CLOSE_REASON_LIST?.map((item) => ({
													...item,
													value: item?.value
												}))}
												title="Select review status"
												selectedItem={taskValues?.closedReason?.value}
												onItemClick={(item) => handleChange(item, 'closedReason')}
												isAddNew={isAdmin()}
											/>
											{errors?.closedReason && (
												<span className="error">{errors?.closedReason}</span>
											)}
										</div>
									</div>
								)}
								{isOrderSampleReview() && (
									<>
										<div className="col-md-12 pr-2">
											<div className="form-group">
												<label htmlFor="brand">Sample type</label>
												<CustomDropdown
													items={fitTypes?.map((item) => ({
														...item,
														id: item?.constant,
														value: replaceUnderLine(item?.name)
													}))}
													title="Select brand"
													selectedItem={taskValues?.fitTypes?.value}
													onItemClick={(item) => handleChange(item, 'fitTypes')}
												/>
											</div>
										</div>
										<div className="col-md-12 pr-2">
											<div className="form-group">
												<label htmlFor="brand">Review status</label>
												<CustomDropdown
													items={
														reviewStatusFilter()
															? TASK_REVIEW_STATUS_NEW
															: TASK_REVIEW_STATUS?.map((item) => ({
																	...item,
																	value: item?.value
																}))
													}
													title="Select review status"
													selectedItem={taskValues?.reviewStatus?.value}
													onItemClick={(item) => handleChange(item, 'reviewStatus')}
												/>
											</div>
										</div>
										{taskValues?.reviewStatus?.id === 'REJECTED' && (
											<div className="col-md-12 pr-2">
												<InputField
													placeholder="Reject reason*"
													type="text"
													value={taskValues?.rejectReason}
													onChange={(e) =>
														handleChange(e.target.value, 'rejectReason')
													}
													labelTitle="Reject reason*"
													errors={errors?.rejectReason}
												/>
											</div>
										)}
									</>
								)}
								{(isPresentedToTheBuyer() || isBuyerInitiallySelected()) && (
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<label htmlFor="brand">Buyer brand name*</label>
											<CustomDropdown
												items={brandsList}
												title="Select brand"
												selectedItem={taskValues?.brand?.name}
												onItemClick={(item) => handleChange(item, 'brand')}
											/>
											{errors?.brand && <span className="error">{errors?.brand}</span>}
										</div>
									</div>
								)}
								{isConnectProductToDeal() && (
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<label htmlFor="deal">Deal*</label>
											<CustomDropdown
												items={dealOptions}
												title="Select deal"
												selectedItem={taskValues?.deal?.dealName}
												onItemClick={(item) => handleChange(item, 'deal')}
												toolTipLength={25}
											/>
											{errors?.deal && <span className="error">{errors?.deal}</span>}
										</div>
									</div>
								)}
								{isSetPriority() && (
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<label htmlFor="brand">Priority type*</label>
											<CustomDropdown
												items={TASK_PRIORITY_OPTIONS}
												title="Select priority type"
												selectedItem={taskValues?.priorityType?.value}
												onItemClick={(item) => handleChange(item, 'priorityType')}
											/>
											{/* {taskValues?.priorityType?.value && (
												<p className="mt-1">
													{taskValues?.priorityType?.description}
												</p>
											)} */}
											{errors?.priorityType && (
												<span className="error">{errors?.priorityType}</span>
											)}
										</div>
									</div>
								)}
								<div className="col-md-12 pr-2">
									<textarea
										ref={commentRef}
										className="form-control"
										placeholder="Comment"
										name="comment"
										value={taskValues?.comment}
										onChange={(e) => {
											commentRef.current.style.height = 'auto';
											commentRef.current.style.height = `${commentRef.current.scrollHeight}px`;
											handleChange(e.target.value, 'comment');
										}}
									/>
								</div>
							</div>
						</div>
						<div className="modal-footer">
							<div className="footer-buttons text-right">
								<button className="button outline size32 ml-2" onClick={onClose}>
									Cancel
								</button>
								<button
									className="button size32 ml-2"
									onClick={handleSubmit}
									disabled={isLoading}
								>
									Update
								</button>
							</div>
						</div>
					</div>
				</div>
			</ModalContainer>
		</LoaderComponent>
	);
};

export default AddTaskModal;
